import { SEED } from "./seed";
import { isTripData } from "./validateTrip";
import type { DayPlan, ScheduleItem, TripData, VoteTally } from "../types";

export function scoreVotes(tally?: VoteTally): number {
  if (!tally) return 0;
  return (tally.michael ?? 0) + (tally.suzy ?? 0);
}

/**
 * Overwrite public PII / reservation-stealable fields from seed so older
 * localStorage / imports stop showing full names, emails, and booking refs.
 */
export function scrubPrivacyFromSeed(data: TripData): TripData {
  const seedDayByDate = new Map(SEED.days.map((d) => [d.date, d]));
  const seedPinById = new Map(SEED.pins.map((p) => [p.id, p]));

  const days: DayPlan[] = data.days.map((d) => {
    const seedDay = seedDayByDate.get(d.date);
    if (!seedDay) {
      return {
        ...d,
        items: d.items.map(stripItemBookingRef),
      };
    }
    const seedItemById = new Map(seedDay.items.map((i) => [i.id, i]));
    const seedTodoById = new Map(seedDay.todos.map((t) => [t.id, t]));
    return {
      ...d,
      title: seedDay.title,
      summary: seedDay.summary ?? d.summary,
      location: d.location ?? seedDay.location,
      items: d.items.map((item) => {
        const seedItem = seedItemById.get(item.id);
        if (!seedItem) return stripItemBookingRef(item);
        return {
          ...item,
          title: seedItem.title,
          detail: seedItem.detail,
          link: seedItem.link,
          time: item.time ?? seedItem.time,
          endTime: item.endTime ?? seedItem.endTime,
          bookingRef: undefined,
        };
      }),
      todos: d.todos.map((todo) => {
        const seedTodo = seedTodoById.get(todo.id);
        return seedTodo ? { ...todo, text: seedTodo.text } : todo;
      }),
    };
  });

  return {
    ...data,
    meta: {
      ...data.meta,
      travelers: structuredClone(SEED.meta.travelers),
      notes: structuredClone(SEED.meta.notes),
    },
    days,
    pins: data.pins.map((p) => {
      const seed = seedPinById.get(p.id);
      if (!seed) return p;
      return { ...p, notes: seed.notes ?? p.notes };
    }),
    version: Math.max(data.version ?? 0, SEED.version),
  };
}

function stripItemBookingRef(item: ScheduleItem): ScheduleItem {
  if (item.bookingRef === undefined) return item;
  const { bookingRef: _removed, ...rest } = item;
  return rest;
}

/** Merge seed pin photos + mapsQuery (and missing pins) into stored trip data. */
export function mergeSeedPinImages(data: TripData): TripData {
  const byId = new Map(SEED.pins.map((p) => [p.id, p]));
  const known = new Set(data.pins.map((p) => p.id));
  const missing = SEED.pins.filter((p) => !known.has(p.id));
  return {
    ...data,
    version: Math.max(data.version ?? 0, SEED.version),
    pins: [
      ...data.pins.map((p) => {
        const seed = byId.get(p.id);
        if (!seed) return p;
        // Always refresh seed image + mapsQuery so seed maps/photo fields land in localStorage.
        // Union day ids so a new work-block day still highlights an existing cafe pin.
        return {
          ...p,
          imageUrl: seed.imageUrl ?? p.imageUrl,
          imageAlt: seed.imageAlt ?? p.imageAlt,
          imageCredit: seed.imageCredit ?? p.imageCredit,
          mapsQuery: seed.mapsQuery ?? p.mapsQuery,
          placeId: seed.placeId ?? p.placeId,
          dayIds: unionDayIds(p.dayIds, seed.dayIds),
        };
      }),
      ...missing,
    ],
  };
}

function unionDayIds(current?: string[], seed?: string[]): string[] | undefined {
  const out: string[] = [];
  for (const id of [...(current ?? []), ...(seed ?? [])]) {
    if (!out.includes(id)) out.push(id);
  }
  return out.length > 0 ? out : undefined;
}


const WORK_ITEM_IDS = new Set([
  "work-bicka-16",
  "work-puelo-17",
  "work-super-18",
  "work-singular-19",
]);

/** Cafe work rows sit in the morning, before the day's far trip. */
function placeMorningWork<T extends { id: string; time?: string; pinId?: string }>(
  items: T[],
): T[] {
  const work = items.filter((i) => WORK_ITEM_IDS.has(i.id));
  if (work.length === 0) {
    return items.map((i) =>
      i.id === "lunch-bellavista-16" && !i.pinId
        ? { ...i, pinId: "pin-bellavista" }
        : i,
    );
  }
  const rest = items.filter((i) => !WORK_ITEM_IDS.has(i.id));
  const stamped = work.map((i) => ({ ...i, time: "morning" as const }));
  let insertAt = 0;
  while (insertAt < rest.length && !rest[insertAt].time) insertAt += 1;
  const merged = [
    ...rest.slice(0, insertAt),
    ...stamped,
    ...rest.slice(insertAt),
  ];
  return merged.map((i) =>
    i.id === "lunch-bellavista-16" && !i.pinId
      ? { ...i, pinId: "pin-bellavista" }
      : i,
  );
}

/** Bring older localStorage / import payloads up to current seed (ideas + votes + photos + privacy). */
export function migrateTripData(raw: unknown): TripData | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Partial<TripData>;
  if (!Array.isArray(data.days) || !Array.isArray(data.pins) || !data.meta) {
    return null;
  }

  const seedByDate = new Map(SEED.days.map((d) => [d.date, d]));
  const days = data.days.map((d) => {
    const seedDay = seedByDate.get(d.date);
    if (!seedDay) return d;
    const thinDay = d.items.length <= 1 && seedDay.items.length > 1;
    if (thinDay) {
      const byId = new Map(d.items.map((i) => [i.id, i]));
      return {
        ...d,
        title: seedDay.title,
        summary: seedDay.summary,
        location: d.location ?? seedDay.location,
        items: placeMorningWork(seedDay.items.map((i) => byId.get(i.id) ?? i)),
        todos: [
          ...seedDay.todos.map((t) => d.todos.find((x) => x.id === t.id) ?? t),
          ...d.todos.filter((t) => !seedDay.todos.some((s) => s.id === t.id)),
        ],
      };
    }
    const itemIds = new Set(d.items.map((i) => i.id));
    const todoIds = new Set(d.todos.map((t) => t.id));
    return {
      ...d,
      summary: d.summary ?? seedDay.summary,
      location: d.location ?? seedDay.location,
      items: placeMorningWork([
        ...d.items,
        ...seedDay.items.filter((i) => !itemIds.has(i.id)),
      ]),
      todos: [...d.todos, ...seedDay.todos.filter((t) => !todoIds.has(t.id))],
    };
  });

  const existingIdeas = Array.isArray(data.ideas) ? data.ideas : [];
  const ideaIds = new Set(existingIdeas.map((i) => i.id));
  const ideas = [
    ...existingIdeas,
    ...SEED.ideas.filter((i) => !ideaIds.has(i.id)),
  ];

  const candidate: TripData = {
    meta: data.meta as TripData["meta"],
    days,
    pins: data.pins,
    ideas,
    votes:
      data.votes && typeof data.votes === "object" && !Array.isArray(data.votes)
        ? data.votes
        : {},
    version: Math.max(Number(data.version) || 0, SEED.version),
  };

  const merged = scrubPrivacyFromSeed(mergeSeedPinImages(candidate));
  return isTripData(merged) ? merged : null;
}
